/**
 * Sectioned member profile (Phase 4).
 * Reads /api/profile/sections and PATCH /api/profile/sections/:section per tab.
 * Sections A - F with in-tab save. Reuses existing shadcn Card/Input/Button.
 *
 * Read-only admin preview mode: if the URL has ?admin_preview=<user_id>
 * and the current user is super_admin, the page fetches the target user's
 * profile via /admin/users/{id}/profile-view and renders every form
 * disabled with an amber banner at the top. Non-super_admin visitors of
 * that URL are redirected to their own profile with an access-denied toast.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Save, Check, Sparkles, Upload, X, LogOut, Settings, Eye } from 'lucide-react';
import { toast } from 'sonner';
import DostPolishModal from '@/components/dost/DostPolishModal';
import ProfileInsights from '@/components/assessments/ProfileInsights';
import ProgressionHero from '@/components/progression/ProgressionHero';
import AchievementsPanel from '@/components/achievements/AchievementsPanel';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const CommaField = ({ value, onChange, disabled, readOnly, ...rest }) => (
  <Input value={Array.isArray(value) ? value.join(', ') : (value || '')}
         onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
         disabled={disabled}
         readOnly={readOnly}
         {...rest} />
);

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs uppercase tracking-widest text-secondary-700 mb-1 block">{label}</Label>
    {children}
  </div>
);

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewUserId = searchParams.get('admin_preview');
  const previewReason = searchParams.get('reason') || '';
  const isSuperAdmin = user?.role === 'super_admin';
  const isPreview = !!previewUserId && isSuperAdmin;
  const readOnly = isPreview;
  const [previewTarget, setPreviewTarget] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  // C.2 tack-on: unlocked achievements count (excludes hidden secrets)
  // for the side-nav badge.
  const [achievementCount, setAchievementCount] = useState(null);
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Guard: non-super_admin visiting a preview URL gets redirected with a toast.
  useEffect(() => {
    if (previewUserId && user && !isSuperAdmin) {
      toast.error('Access denied. Read-only preview is super-admin only.');
      navigate('/profile', { replace: true });
    }
  }, [previewUserId, user, isSuperAdmin, navigate]);

  useEffect(() => {
    (async () => {
      try {
        if (isPreview) {
          const params = new URLSearchParams();
          if (previewReason) params.set('reason', previewReason);
          const qs = params.toString();
          const { data } = await api.get(
            `/admin/users/${previewUserId}/profile-view${qs ? `?${qs}` : ''}`,
          );
          setPayload(data);
          setPreviewTarget(data.target_user || null);
        } else {
          const { data } = await api.get('/profile/sections');
          setPayload(data);
        }
      } finally { setLoading(false); }
    })();
  }, [isPreview, previewUserId, previewReason]);

  useEffect(() => {
    if (isPreview) return;
    (async () => {
      try {
        const { data } = await api.get('/me/achievements');
        // `total_public` = unlocked - hidden secrets. Matches what the
        // user actually sees under the Achievements tab.
        setAchievementCount(data.total_public ?? data.total_unlocked ?? 0);
      } catch { /* silent */ }
    })();
  }, [isPreview]);

  const savePart = async (section, part) => {
    if (readOnly) return;
    setSaving(true);
    try {
      await api.patch(`/profile/sections/${section}`, part);
      const { data } = await api.get('/profile/sections');
      setPayload(data);
      setSaved(section);
      setTimeout(() => setSaved(''), 1500);
    } finally { setSaving(false); }
  };

  if (loading || !payload) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" /></div>;

  const s = payload.sections;
  const c = payload.completion_by_section;
  const heroUser = isPreview
    ? {
        first_name: previewTarget?.first_name,
        last_name: previewTarget?.last_name,
        email: previewTarget?.email,
      }
    : user;

  return (
    <div className="p-8 max-w-4xl">
      {isPreview && (
        <div
          className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
          data-testid="admin-preview-banner"
          role="alert"
        >
          <Eye className="h-5 w-5 text-amber-800 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">
              Read-only admin preview of{' '}
              <span data-testid="admin-preview-banner-email">
                {previewTarget?.email || 'user'}
              </span>
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Any changes you make will not save.
              {previewReason ? ` Reason: ${previewReason}` : ''}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (previewUserId) {
                navigate(`/admin/users/${previewUserId}`);
              } else {
                window.close();
              }
            }}
            className="border-amber-400 text-amber-900 hover:bg-amber-100"
            data-testid="admin-preview-close-btn"
          >
            Close preview
          </Button>
        </div>
      )}
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={isPreview}>
              <button
                type="button"
                aria-label="Open account menu"
                data-testid="profile-hero-avatar-trigger"
                disabled={isPreview}
                className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center text-white font-display text-2xl font-bold shadow-soft cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary-400 hover:opacity-90 transition-opacity disabled:cursor-not-allowed disabled:opacity-80"
              >
                {(heroUser?.first_name?.[0] || heroUser?.email?.[0] || '?').toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="truncate">
                <div className="text-sm font-semibold text-primary-900 truncate">
                  {(() => {
                    const fn = (heroUser?.first_name || '').trim();
                    const ln = (heroUser?.last_name || '').trim();
                    return [fn, ln].filter(Boolean).join(' ') || heroUser?.email;
                  })()}
                </div>
                <div className="text-xs text-neutral-500 truncate font-normal">{heroUser?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings/security')} data-testid="profile-hero-menu-security">
                <Settings className="h-4 w-4 mr-2" /> Security
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="profile-hero-menu-logout"
                className="text-accent-700 focus:text-accent-800"
              >
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div>
            <h1 className="font-display text-3xl font-bold text-primary-900" data-testid="profile-hero-display-name">
              {(() => {
                const fn = (heroUser?.first_name || '').trim();
                const ln = (heroUser?.last_name || '').trim();
                const pref = (heroUser?.preferred_name || '').trim();
                const full = [pref || fn, ln].filter(Boolean).join(' ');
                if (full) return full;
                const local = (heroUser?.email || '').split('@')[0];
                return local || 'Complete your profile';
              })()}
            </h1>
            <p className="text-neutral-600 text-sm">{heroUser?.email}</p>
          </div>
        </div>
        <div className="max-w-md">
          <div className="flex items-center justify-between text-xs text-neutral-500 mb-1">
            <span>Profile completion</span><span>{payload.overall_completion}%</span>
          </div>
          <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-secondary-500 to-secondary-600" style={{ width: `${payload.overall_completion}%` }} />
          </div>
        </div>
      </div>

      {/* F2.c Progression hero - rank, level, points, recent activity */}
      {!isPreview && (
        <div className="mb-8">
          <ProgressionHero />
        </div>
      )}

      <Tabs defaultValue="basics" className="w-full">
        {/* Mobile: horizontal scroll with fade masks. Desktop: vertical side-nav. */}
        <div className="md:flex md:gap-6">
          <div
            className="relative md:w-56 md:shrink-0 md:sticky md:top-4 md:self-start"
            data-testid="profile-tabs-nav"
          >
            {/* Mobile fade edges. Purely decorative, pointer-events none. */}
            <div className="md:hidden absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" data-testid="profile-tabs-fade-left" />
            <div className="md:hidden absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" data-testid="profile-tabs-fade-right" />
            <TabsList
              className="flex overflow-x-auto md:overflow-visible gap-1 bg-transparent p-0 h-auto w-full md:flex-col md:items-stretch md:bg-neutral-50 md:rounded-xl md:p-2 md:border md:border-neutral-200"
              data-testid="profile-tabs-list"
            >
              {[
                ['basics', 'Basics'],
                ['connection_intentions', 'Intentions'],
                ['lifestyle', 'Lifestyle'],
                ['background', 'Background'],
                ['intro_prefs', 'Introductions'],
                ['privacy', 'Privacy'],
                ['socials', 'Social profiles'],
                ['insights', 'Insights'],
                ['achievements', 'Achievements'],
              ].map(([key, label]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="shrink-0 md:shrink whitespace-nowrap md:whitespace-normal md:justify-start md:text-left md:w-full md:px-3 md:py-2 md:rounded-lg md:data-[state=active]:bg-white md:data-[state=active]:shadow-sm md:data-[state=active]:text-primary-900 md:data-[state=active]:border-l-4 md:data-[state=active]:border-secondary-500 flex flex-col md:flex-row md:items-center md:gap-3 gap-0.5"
                  data-testid={`profile-tab-${key}`}
                >
                  <span className="text-sm font-medium">{label}</span>
                  {/* C.2: Achievements tab shows the unlocked (non-secret) count as an
                      ember pill so users glance-see their progress. Other tabs keep
                      the completion-percentage indicator. */}
                  {key === 'achievements' ? (
                    achievementCount != null && achievementCount > 0 ? (
                      <span
                        className="text-[10px] font-semibold md:ml-auto px-1.5 py-0.5 rounded-full bg-secondary-500 text-primary-900 min-w-[18px] text-center"
                        data-testid="profile-tab-achievements-count"
                      >
                        {achievementCount}
                      </span>
                    ) : null
                  ) : (
                    <span className="text-[10px] text-neutral-400 md:ml-auto md:text-xs font-mono">
                      {key === 'insights' ? '' : `${c[key]}%`}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="flex-1 min-w-0">

        <TabsContent value="basics" className="pt-6">
          <SectionCard title="Section A - Basics" saved={saved === 'basics'}>
            <BasicsForm data={s.basics} derived={payload.derived} onSave={(p) => savePart('basics', p)} saving={saving} readOnly={readOnly} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="connection_intentions" className="pt-6">
          <SectionCard title="Section B - Connection Intentions" saved={saved === 'connection_intentions'}>
            <IntentionsForm data={s.connection_intentions} onSave={(p) => savePart('connection_intentions', p)} saving={saving} readOnly={readOnly} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="lifestyle" className="pt-6">
          <SectionCard title="Section C - Life & Lifestyle" saved={saved === 'lifestyle'}>
            <LifestyleForm data={s.lifestyle} onSave={(p) => savePart('lifestyle', p)} saving={saving} readOnly={readOnly} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="background" className="pt-6">
          <SectionCard title="Section D - Background & Values" saved={saved === 'background'}>
            <BackgroundForm data={s.background} onSave={(p) => savePart('background', p)} saving={saving} readOnly={readOnly} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="intro_prefs" className="pt-6">
          <SectionCard title="Section E - Introduction Preferences" saved={saved === 'intro_prefs'}>
            <IntroPrefsForm data={s.intro_prefs} onSave={(p) => savePart('intro_prefs', p)} saving={saving} readOnly={readOnly} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="privacy" className="pt-6">
          <SectionCard title="Section F - Privacy & Consent" saved={saved === 'privacy'}>
            <PrivacyForm data={s.privacy} onSave={(p) => savePart('privacy', p)} saving={saving} readOnly={readOnly} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="socials" className="pt-6" data-testid="socials-tab-content">
          <SectionCard title="Section G - Social profiles" saved={saved === 'socials'}>
            <SocialsForm data={s.socials || {}} onSave={(p) => savePart('socials', p)} saving={saving} readOnly={readOnly} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="insights" className="pt-6" data-testid="insights-tab-content">
          <SectionCard title="Section H - Profile Insights">
            <ProfileInsights />
          </SectionCard>
        </TabsContent>

        <TabsContent value="achievements" className="pt-6" data-testid="achievements-tab-content">
          <SectionCard title="Section I - Achievements">
            <AchievementsPanel />
          </SectionCard>
        </TabsContent>
          </div>
        </div>
      </Tabs>

      {/* Bundle 36 Task B - user-facing recent account activity.
          Transparency layer for admin edit-on-behalf actions. */}
      <RecentAccountActivity />
    </div>
  );
};

const RecentAccountActivity = () => {
  const [open, setOpen] = React.useState(false);
  const [entries, setEntries] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get('/users/me/recent-activity?limit=20');
      setEntries(data.entries || []);
    } catch (_) {
      // silent - optional widget
    } finally {
      setLoaded(true);
    }
  }, []);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) load();
  };
  const relTime = (iso) => {
    if (!iso) return '';
    try {
      const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
      if (s < 60) return `${s}s ago`;
      const m = Math.round(s / 60);
      if (m < 60) return `${m}m ago`;
      const h = Math.round(m / 60);
      if (h < 48) return `${h}h ago`;
      return `${Math.round(h / 24)}d ago`;
    } catch (_) { return ''; }
  };
  return (
    <div className="mt-10 border-t border-neutral-200 pt-6"
          data-testid="profile-recent-activity">
      <button
        type="button"
        onClick={toggle}
        className="w-full text-left text-sm font-semibold text-primary-900 flex items-center justify-between hover:text-secondary-700"
        data-testid="profile-recent-activity-toggle"
      >
        <span>Recent account activity</span>
        <span className="text-xs text-neutral-500">{open ? 'Hide' : 'Show'}</span>
      </button>
      <p className="text-[11px] text-neutral-500 mt-1">
        We keep a record of any updates our team makes to your profile. If
        anything looks wrong, contact us.
      </p>
      {open && (
        <div className="mt-3" data-testid="profile-recent-activity-list">
          {!loaded ? (
            <div className="text-xs text-neutral-400">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-xs text-neutral-500 italic"
                  data-testid="profile-recent-activity-empty">
              No admin actions on your account.
            </div>
          ) : (
            <ul className="space-y-2">
              {entries.map((e, i) => (
                <li
                  key={i}
                  className="text-xs p-2 rounded border border-neutral-100 bg-neutral-50"
                  data-testid={`profile-recent-activity-entry-${i}`}
                >
                  <div className="text-primary-900 font-medium">
                    {e.action_label}
                  </div>
                  {(e.fields_changed || []).length > 0 && (
                    <div className="text-neutral-600 mt-0.5">
                      Fields: {(e.fields_changed || []).join(', ')}
                    </div>
                  )}
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    {relTime(e.timestamp)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const SectionCard = ({ title, saved, children }) => (
  <Card className="border shadow-soft">
    <CardContent className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <h2 className="font-display text-xl font-bold text-primary-900">{title}</h2>
        {saved && <span className="text-xs text-success-700 flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>}
      </div>
      {children}
    </CardContent>
  </Card>
);

const SaveBar = ({ onSave, saving, readOnly }) => {
  if (readOnly) return null;
  return (
    <div className="pt-4 mt-4 border-t border-neutral-100">
      <Button onClick={onSave} disabled={saving} className="bg-primary-800 hover:bg-primary-900 text-white">
        <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save section'}
      </Button>
    </div>
  );
};

/* ---- Section A ---- */
const BasicsForm = ({ data, derived, onSave, saving, readOnly = false }) => {
  const { user } = useAuth();
  const dob = derived?.dob || null;
  const ageFromDob = derived?.age_from_dob;
  const hasDob = !!dob && ageFromDob != null;
  const [f, setF] = useState({
    first_name: data.first_name || '',
    last_name: data.last_name || '',
    preferred_name: data.preferred_name || '',
    age: data.age || '', gender: data.gender || '',
    city: data.location?.city || '', state: data.location?.state || '',
    country: data.location?.country || '',
    occupation: data.occupation || '', education: data.education || '',
    height: data.height || '',
    about_me: data.about_me || '',
    photos: data.photos || { primary: null, additional: [] },
  });
  const [dostOpen, setDostOpen] = useState(false);
  const [nameError, setNameError] = useState('');
  const submit = () => {
    const fn = (f.first_name || '').trim();
    const ln = (f.last_name || '').trim();
    if (!fn || !ln) {
      setNameError('First name and last name are required.');
      return;
    }
    setNameError('');
    onSave({
      first_name: fn,
      last_name: ln,
      preferred_name: (f.preferred_name || '').trim() || null,
      age: Number(f.age) || null,
      gender: f.gender || null,
      location: { city: f.city, state: f.state, country: f.country },
      occupation: f.occupation, education: f.education,
      height: Number(f.height) || null,
      about_me: (f.about_me || '').trim() || null,
      photos: f.photos,
    });
  };
  const aboutIsEmpty = !(f.about_me || '').trim();
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3" data-testid="profile-basics-name-row">
        <Field label="First name">
          <Input
            value={f.first_name}
            onChange={(e) => setF({ ...f, first_name: e.target.value })}
            placeholder="Your first name"
            data-testid="profile-first-name-input"
            disabled={readOnly}
            readOnly={readOnly}
          />
        </Field>
        <Field label="Last name">
          <Input
            value={f.last_name}
            onChange={(e) => setF({ ...f, last_name: e.target.value })}
            placeholder="Your last name"
            data-testid="profile-last-name-input"
            disabled={readOnly}
            readOnly={readOnly}
          />
        </Field>
        <Field label="Preferred name (optional)">
          <Input
            value={f.preferred_name}
            onChange={(e) => setF({ ...f, preferred_name: e.target.value })}
            placeholder="What friends call you"
            data-testid="profile-preferred-name-input"
            disabled={readOnly}
            readOnly={readOnly}
          />
        </Field>
      </div>
      {nameError && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-xs"
          data-testid="profile-name-error"
        >
          {nameError}
        </div>
      )}
      <Field label="About you">
        <Textarea
          rows={5}
          value={f.about_me}
          onChange={e => setF({ ...f, about_me: e.target.value })}
          placeholder="A few sentences about who you are and what you're excited about."
          data-testid="profile-about-me-textarea"
          disabled={readOnly}
          readOnly={readOnly}
        />
        {!readOnly && (
          <div className="mt-2 flex items-center justify-end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => !aboutIsEmpty && setDostOpen(true)}
                      disabled={aboutIsEmpty}
                      className="border-secondary-300 text-secondary-800 hover:bg-secondary-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="profile-ask-dost-btn"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Ask Dost to polish
                    </Button>
                  </span>
                </TooltipTrigger>
                {aboutIsEmpty && (
                  <TooltipContent side="left">
                    <span data-testid="profile-ask-dost-tooltip">Write a few sentences first, then Dost can polish them.</span>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </Field>
      <ProfilePhotoGrid
        userId={user?.id}
        photos={f.photos}
        onChange={(next) => setF((cur) => ({ ...cur, photos: next }))}
        readOnly={readOnly}
      />
      <div className="grid gap-4 md:grid-cols-3">
        {hasDob ? (
          <Field label="Age">
            <div
              className="h-10 px-3 py-2 rounded-md border border-neutral-200 bg-neutral-50 text-sm text-primary-900 flex items-center"
              data-testid="profile-age-computed"
            >
              {ageFromDob}
              <span className="ml-2 text-[10px] text-neutral-500">from DOB {dob}</span>
            </div>
          </Field>
        ) : (
          <Field label="Age">
            <Input type="number" value={f.age} onChange={e => setF({ ...f, age: e.target.value })} data-testid="profile-age-input" disabled={readOnly} readOnly={readOnly} />
          </Field>
        )}
        <Field label="Gender">
          <Select value={f.gender || undefined} onValueChange={(v) => setF({ ...f, gender: v })} disabled={readOnly}>
            <SelectTrigger data-testid="profile-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {/* C.1c: canonical values are ONLY male / female. */}
              <SelectItem value="male" data-testid="profile-gender-male">Male</SelectItem>
              <SelectItem value="female" data-testid="profile-gender-female">Female</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Height (cm)"><Input type="number" value={f.height} onChange={e => setF({ ...f, height: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="City"><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="State"><Input value={f.state} onChange={e => setF({ ...f, state: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Country"><Input value={f.country} onChange={e => setF({ ...f, country: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Occupation"><Input value={f.occupation} onChange={e => setF({ ...f, occupation: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Education"><Input value={f.education} onChange={e => setF({ ...f, education: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
      </div>
      <SaveBar onSave={submit} saving={saving} readOnly={readOnly} />
      {!readOnly && (
        <DostPolishModal
          open={dostOpen}
          onClose={() => setDostOpen(false)}
          originalText={f.about_me}
          onAccept={(polished) => setF((cur) => ({ ...cur, about_me: polished }))}
        />
      )}
    </div>
  );
};

/* ---- Profile photos (Section A companion) ----
 * Uploads to /media/upload as owner_type=user_profile (private, signed URL).
 * The <img> requests are authenticated by either the session_token cookie
 * or the Bearer JWT that axios attaches - both are handled by media_routes.
 */
const ProfilePhotoGrid = ({ userId, photos, onChange, readOnly = false }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [broken, setBroken] = useState({});
  const primary = photos?.primary || null;
  const additional = Array.isArray(photos?.additional) ? photos.additional : [];
  const all = [primary, ...additional].filter(Boolean);
  const canAddMore = !readOnly && all.length < 6;

  const upload = async (files) => {
    setErr('');
    setUploading(true);
    const nextUrls = [...all];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('owner_type', 'user_profile');
        if (userId) fd.append('owner_id', userId);
        fd.append('is_public', 'false');
        const { data } = await api.post('/media/upload/v2', fd);
        nextUrls.push(data.url);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Upload failed');
      }
    }
    onChange({ primary: nextUrls[0] || null, additional: nextUrls.slice(1) });
    setUploading(false);
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - all.length);
    e.target.value = '';
    if (!files.length) return;
    await upload(files);
  };

  const removeAt = (idx) => {
    const next = all.filter((_, i) => i !== idx);
    onChange({ primary: next[0] || null, additional: next.slice(1) });
  };

  return (
    <Field label="Your photos">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {all.map((url, i) => (
          <div key={`${url}-${i}`} className="relative aspect-square rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50">
            {broken[i] ? (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-100 via-primary-50 to-neutral-100 text-secondary-800/80 font-semibold text-xs">
                Photo
              </div>
            ) : (
              <img
                data-testid={`profile-photo-${i}`}
                src={url}
                alt={`photo-${i}`}
                loading="lazy"
                onError={() => setBroken((s) => ({ ...s, [i]: true }))}
                className="w-full h-full object-cover"
              />
            )}
            {i === 0 && (
              <span className="absolute bottom-1 left-1 text-[10px] bg-secondary-500 text-primary-900 font-semibold px-1.5 py-0.5 rounded">Primary</span>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm"
                aria-label="Remove"
                data-testid={`profile-photo-remove-${i}`}
              ><X className="h-3 w-3 text-accent-700" /></button>
            )}
          </div>
        ))}
        {canAddMore && (
          <label
            className={`aspect-square rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center text-neutral-500 text-xs cursor-pointer hover:border-secondary-400 hover:text-secondary-700 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            data-testid="profile-photo-upload-tile"
          >
            <Upload className="h-5 w-5 mb-1" />
            <span>{uploading ? 'Uploading…' : 'Add photo'}</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFile} />
          </label>
        )}
      </div>
      {err && <div className="text-xs text-accent-700 mt-2" data-testid="profile-photo-error">{err}</div>}
      <p className="text-xs text-neutral-500 mt-2">Up to 6 photos. The first one is your primary. JPG / PNG / WebP up to 5 MB each.</p>
    </Field>
  );
};

/* ---- Section B ---- */
const IntentionsForm = ({ data, onSave, saving, readOnly = false }) => {
  const [f, setF] = useState({
    connection_intentions: data.connection_intentions || [],
    social_energy: data.social_energy || '',
    preferred_group_size: data.preferred_group_size || '',
    preferred_intro_style: data.preferred_intro_style || '',
    willing_to_travel_solo: data.willing_to_travel_solo ?? true,
  });
  const INTENTS = ['friendship','community','marriage','travel_buddies'];
  const toggle = (v) => {
    if (readOnly) return;
    setF({ ...f, connection_intentions: f.connection_intentions.includes(v) ? f.connection_intentions.filter(x => x !== v) : [...f.connection_intentions, v] });
  };
  return (
    <div className="space-y-4">
      <Field label="What are you looking for?">
        <div className="flex flex-wrap gap-2">
          {INTENTS.map(v => {
            const on = f.connection_intentions.includes(v);
            return <button key={v} type="button" onClick={() => toggle(v)} disabled={readOnly}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all disabled:cursor-not-allowed disabled:opacity-70 ${on ? 'bg-gradient-to-r from-secondary-100 to-secondary-50 border-secondary-200 text-primary-800 shadow-sm' : 'bg-white border-neutral-200 text-neutral-600 hover:border-secondary-300'}`}>{v.replace(/_/g,' ')}</button>;
          })}
        </div>
      </Field>
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Social energy">
          <Select value={f.social_energy || undefined} onValueChange={(v) => setF({ ...f, social_energy: v })} disabled={readOnly}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{['introverted','ambiverted','extroverted','depends'].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Preferred group size">
          <Select value={f.preferred_group_size || undefined} onValueChange={(v) => setF({ ...f, preferred_group_size: v })} disabled={readOnly}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{['small','medium','large'].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Intro style">
          <Select value={f.preferred_intro_style || undefined} onValueChange={(v) => setF({ ...f, preferred_intro_style: v })} disabled={readOnly}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{['warm_organic','organizer_facilitated','either'].map(x => <SelectItem key={x} value={x}>{x.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <label className="flex items-center justify-between p-3 rounded-lg border border-neutral-100 bg-neutral-50/40">
        <span className="text-sm text-primary-900">Open to attending events / trips solo</span>
        <Switch checked={!!f.willing_to_travel_solo} onCheckedChange={(v) => setF({ ...f, willing_to_travel_solo: v })} disabled={readOnly} />
      </label>
      <SaveBar onSave={() => onSave(f)} saving={saving} readOnly={readOnly} />
    </div>
  );
};

/* ---- Section C ---- */
const LifestyleForm = ({ data, onSave, saving, readOnly = false }) => {
  const [f, setF] = useState({
    hobbies: data.hobbies || [], interests: data.interests || [],
    activity_preferences: data.activity_preferences || [],
    smoking_comfort: data.smoking_comfort || '', hookah_comfort: data.hookah_comfort || '',
    pets: data.pets || '', kids: data.kids || '',
    marriage_timeline: data.marriage_timeline || '',
    comm_style: data.comm_style || '', weekend_prefs: data.weekend_prefs || '',
  });
  return (
    <div className="space-y-4">
      <Field label="Hobbies (comma-separated)"><CommaField value={f.hobbies} onChange={(v) => setF({ ...f, hobbies: v })} disabled={readOnly} readOnly={readOnly} /></Field>
      <Field label="Interests (comma-separated)"><CommaField value={f.interests} onChange={(v) => setF({ ...f, interests: v })} disabled={readOnly} readOnly={readOnly} /></Field>
      <Field label="Activity preferences (comma-separated)"><CommaField value={f.activity_preferences} onChange={(v) => setF({ ...f, activity_preferences: v })} disabled={readOnly} readOnly={readOnly} /></Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Smoking comfort"><Input value={f.smoking_comfort} onChange={e => setF({ ...f, smoking_comfort: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Hookah comfort"><Input value={f.hookah_comfort} onChange={e => setF({ ...f, hookah_comfort: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Pets"><Input value={f.pets} onChange={e => setF({ ...f, pets: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Kids"><Input value={f.kids} onChange={e => setF({ ...f, kids: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Marriage timeline"><Input value={f.marriage_timeline} onChange={e => setF({ ...f, marriage_timeline: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Communication style"><Input value={f.comm_style} onChange={e => setF({ ...f, comm_style: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
      </div>
      <Field label="Weekend preferences"><Textarea rows={2} value={f.weekend_prefs} onChange={e => setF({ ...f, weekend_prefs: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
      <SaveBar onSave={() => onSave(f)} saving={saving} readOnly={readOnly} />
    </div>
  );
};

/* ---- Section D ---- */
const BackgroundForm = ({ data, onSave, saving, readOnly = false }) => {
  const [f, setF] = useState({
    ethnicity: data.ethnicity || '', cultural_background: data.cultural_background || '',
    religion: data.religion || '', religious_tradition: data.religious_tradition || '',
    faith_importance: data.faith_importance || '',
    languages_at_home: data.languages_at_home || [],
    languages: data.languages || [],
    personal_values: data.personal_values || [],
    family_orientation: data.family_orientation || '',
  });
  return (
    <div className="space-y-4">
      <p className="text-xs text-neutral-500 mb-2">All fields optional, all private by default.</p>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Ethnicity"><Input value={f.ethnicity} onChange={e => setF({ ...f, ethnicity: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Cultural background"><Input value={f.cultural_background} onChange={e => setF({ ...f, cultural_background: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Religion"><Input value={f.religion} onChange={e => setF({ ...f, religion: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Religious tradition"><Input value={f.religious_tradition} onChange={e => setF({ ...f, religious_tradition: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Faith importance"><Input value={f.faith_importance} onChange={e => setF({ ...f, faith_importance: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        <Field label="Family orientation"><Input value={f.family_orientation} onChange={e => setF({ ...f, family_orientation: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
      </div>
      <Field label="Languages (comma)"><CommaField value={f.languages} onChange={(v) => setF({ ...f, languages: v })} disabled={readOnly} readOnly={readOnly} /></Field>
      <Field label="Languages at home (comma)"><CommaField value={f.languages_at_home} onChange={(v) => setF({ ...f, languages_at_home: v })} disabled={readOnly} readOnly={readOnly} /></Field>
      <Field label="Personal values (comma)"><CommaField value={f.personal_values} onChange={(v) => setF({ ...f, personal_values: v })} disabled={readOnly} readOnly={readOnly} /></Field>
      <SaveBar onSave={() => onSave(f)} saving={saving} readOnly={readOnly} />
    </div>
  );
};

/* ---- Section E ---- */
const IntroPrefsForm = ({ data, onSave, saving, readOnly = false }) => {
  const [f, setF] = useState({
    open_to_organizer_intros: !!data.open_to_organizer_intros,
    intro_age_range: data.intro_age_range || '',
    intro_religion_pref: data.intro_religion_pref || '',
    intro_cultural_pref: data.intro_cultural_pref || '',
    intro_dealbreakers: data.intro_dealbreakers || '',
  });
  return (
    <div className="space-y-4">
      <label className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 bg-secondary-50/40">
        <div>
          <div className="text-sm font-semibold text-primary-900">Allow organizer-facilitated introductions</div>
          <div className="text-xs text-neutral-600">Only shared with organizers if you opt in.</div>
        </div>
        <Switch checked={f.open_to_organizer_intros} onCheckedChange={(v) => setF({ ...f, open_to_organizer_intros: v })} disabled={readOnly} />
      </label>
      {f.open_to_organizer_intros && (
        <>
          <Field label="Preferred age range (e.g. 28-38)"><Input value={f.intro_age_range} onChange={e => setF({ ...f, intro_age_range: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Religion preference"><Input value={f.intro_religion_pref} onChange={e => setF({ ...f, intro_religion_pref: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
            <Field label="Cultural preference"><Input value={f.intro_cultural_pref} onChange={e => setF({ ...f, intro_cultural_pref: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
          </div>
          <Field label="Dealbreakers (optional)"><Textarea rows={2} value={f.intro_dealbreakers} onChange={e => setF({ ...f, intro_dealbreakers: e.target.value })} disabled={readOnly} readOnly={readOnly} /></Field>
        </>
      )}
      <SaveBar onSave={() => onSave(f)} saving={saving} readOnly={readOnly} />
    </div>
  );
};

/* ---- Section F ---- */
const PrivacyForm = ({ data, onSave, saving, readOnly = false }) => {
  const [f, setF] = useState({
    show_full_name: !!data.show_full_name,
    show_last_name_initial_only: !!data.show_last_name_initial_only,
    search_indexable: data.search_indexable !== false,
    visibility_settings: data.visibility_settings || {},
  });
  const dataExport = async () => {
    const { data: exp } = await api.post('/me/data-export');
    const blob = new Blob([JSON.stringify(exp, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'my-dostination-data.json'; a.click();
    URL.revokeObjectURL(url);
  };
  const deleteAccount = async () => {
    if (!window.confirm('Permanently delete your account? This action is scheduled with a 30-day retention window.')) return;
    await api.post('/me/delete-account');
    alert('Account scheduled for deletion.');
  };
  return (
    <div className="space-y-4">
      <label className="flex items-center justify-between p-3 rounded-lg border border-neutral-100 bg-neutral-50/40">
        <span className="text-sm text-primary-900">Show my full last name publicly</span>
        <Switch checked={f.show_full_name} onCheckedChange={(v) => setF({ ...f, show_full_name: v })} disabled={readOnly} />
      </label>
      <label className="flex items-center justify-between p-3 rounded-lg border border-neutral-100 bg-neutral-50/40">
        <span className="text-sm text-primary-900">Show only first name + last initial</span>
        <Switch checked={f.show_last_name_initial_only} onCheckedChange={(v) => setF({ ...f, show_last_name_initial_only: v })} disabled={readOnly} />
      </label>
      <label className="flex items-center justify-between p-3 rounded-lg border border-neutral-100 bg-neutral-50/40">
        <span className="text-sm text-primary-900">Search engines may index my profile</span>
        <Switch checked={f.search_indexable} onCheckedChange={(v) => setF({ ...f, search_indexable: v })} disabled={readOnly} />
      </label>
      <SaveBar onSave={() => onSave(f)} saving={saving} readOnly={readOnly} />
      {!readOnly && (
        <div className="pt-6 mt-6 border-t border-neutral-100 space-y-2">
          <div className="text-xs uppercase tracking-widest text-secondary-700 mb-2">Your data</div>
          <Button variant="outline" onClick={dataExport}>Download my data</Button>
          <Button variant="outline" onClick={deleteAccount} className="text-accent-700 hover:text-accent-900 border-accent-200 ml-2">Delete my account</Button>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;

/* ---- Section G - Social profiles (RSVP <-> profile parity) ---- */
const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', prefix: '@',
    placeholder: 'yourhandle',
    normalize: (v) => v.replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\//i, '') },
  { key: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/',
    placeholder: 'your-name',
    normalize: (v) => v.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, '') },
  { key: 'tiktok', label: 'TikTok', prefix: '@',
    placeholder: 'yourhandle',
    normalize: (v) => v.replace(/^@/, '').replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/i, '') },
  { key: 'facebook', label: 'Facebook', prefix: 'facebook.com/',
    placeholder: 'your.name',
    normalize: (v) => v.replace(/^https?:\/\/(www\.)?facebook\.com\//i, '') },
];

const SocialsForm = ({ data, onSave, saving, readOnly = false }) => {
  const initial = data.social_handles || {};
  const [f, setF] = useState({
    instagram: initial.instagram || '',
    linkedin: initial.linkedin || '',
    tiktok: initial.tiktok || '',
    facebook: initial.facebook || '',
  });
  const submit = () => {
    const cleaned = {};
    SOCIAL_FIELDS.forEach(({ key, normalize }) => {
      const raw = (f[key] || '').trim();
      cleaned[key] = raw ? normalize(raw) : '';
    });
    onSave({ social_handles: cleaned });
  };
  return (
    <div className="space-y-4">
      <p className="text-xs text-neutral-500">
        Optional. These help you connect with people you meet at events.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {SOCIAL_FIELDS.map(({ key, label, prefix, placeholder }) => (
          <Field key={key} label={label}>
            <div className="flex rounded-md border border-neutral-200 overflow-hidden focus-within:ring-2 focus-within:ring-secondary-500 focus-within:border-secondary-400">
              <span className="inline-flex items-center px-2 text-xs text-neutral-500 bg-neutral-50 border-r border-neutral-200 select-none">
                {prefix}
              </span>
              <Input
                value={f[key]}
                onChange={(e) => setF({ ...f, [key]: e.target.value })}
                placeholder={placeholder}
                className="border-0 focus-visible:ring-0"
                data-testid={`profile-social-${key}-input`}
                disabled={readOnly}
                readOnly={readOnly}
              />
            </div>
          </Field>
        ))}
      </div>
      <SaveBar onSave={submit} saving={saving} readOnly={readOnly} />
    </div>
  );
};
